/**
 * OfferPopup — the player-facing offer surface (economy spec §8).
 *
 * An offer card is ONE baked image (the price/CTA is painted into the art); the
 * player just taps the image to act. This overlay shows the live set one card at
 * a time: free + video offers first, then the single random paid offer. An X
 * skips to the next; when the queue empties the popup closes.
 *
 * Actions by kind:
 *   paid  → onPaid(offer)  (triggers the IAP; its reward flyout runs elsewhere)
 *   free  → onClaim(offer) (server grants; result arrives via notifyClaimResult)
 *   video → watch offer.video.ads rewarded ads, then onClaim(offer, adsWatched)
 *
 * Body-level singleton with injected CSS, mirroring ComingSoonOverlay.
 */

import type { OfferWire, OfferClaimResult } from '@sdl/shared';
import type { AdProvider } from '../../iap/AdProvider';

const STYLE_ID = 'sdl-offerpopup-styles';

export interface OfferPopupDeps {
  /** Fire the IAP purchase for a paid offer. */
  onPaid: (offer: OfferWire) => void;
  /** Claim a free / video offer (adsWatched set for video). */
  onClaim: (offer: OfferWire, adsWatched?: number) => void;
  adProvider: AdProvider;
}

export class OfferPopup {
  public element: HTMLElement;
  private cardImg: HTMLImageElement;
  private progressEl: HTMLElement;
  private closeBtn: HTMLButtonElement;
  private deps: OfferPopupDeps;

  private queue: OfferWire[] = [];
  private index = 0;
  private busy = false;
  private pendingClaimId: string | null = null;

  constructor(deps: OfferPopupDeps) {
    this.deps = deps;
    OfferPopup.injectStyles();
    this.element = document.createElement('div');
    this.element.className = 'sdl-offerpopup-host';
    this.element.innerHTML = `
      <div class="sdl-offerpopup-stage" role="dialog" aria-modal="true">
        <button class="sdl-offerpopup-close" aria-label="Close">&times;</button>
        <img class="sdl-offerpopup-card" alt="Offer" />
        <div class="sdl-offerpopup-progress"></div>
      </div>
    `;
    document.body.appendChild(this.element);

    this.cardImg = this.element.querySelector('.sdl-offerpopup-card') as HTMLImageElement;
    this.progressEl = this.element.querySelector('.sdl-offerpopup-progress') as HTMLElement;
    this.closeBtn = this.element.querySelector('.sdl-offerpopup-close') as HTMLButtonElement;

    this.cardImg.addEventListener('click', () => this.act());
    this.closeBtn.addEventListener('click', () => this.advance());
    this.element.addEventListener('click', (e) => {
      if (e.target === this.element) this.advance(); // backdrop tap = skip
    });
  }

  /** Show the given offers in order. No-op if empty or already showing. */
  show(offers: OfferWire[]): void {
    const renderable = offers.filter((o) => !!o.img);
    if (renderable.length === 0 || this.isVisible()) return;
    this.queue = renderable;
    this.index = 0;
    this.element.classList.add('visible');
    this.displayCurrent();
  }

  isVisible(): boolean {
    return this.element.classList.contains('visible');
  }

  private current(): OfferWire | undefined {
    return this.queue[this.index];
  }

  private displayCurrent(): void {
    const offer = this.current();
    if (!offer) {
      this.hide();
      return;
    }
    this.busy = false;
    this.pendingClaimId = null;
    this.setProgress('');
    this.cardImg.classList.remove('busy');
    this.cardImg.src = offer.img;
  }

  private advance(): void {
    if (this.busy) return;
    this.index += 1;
    if (this.index >= this.queue.length) this.hide();
    else this.displayCurrent();
  }

  private hide(): void {
    this.element.classList.remove('visible');
    this.queue = [];
    this.index = 0;
    this.busy = false;
    this.pendingClaimId = null;
  }

  private setProgress(text: string): void {
    this.progressEl.textContent = text;
    this.progressEl.classList.toggle('show', !!text);
  }

  private async act(): Promise<void> {
    const offer = this.current();
    if (!offer || this.busy) return;

    if (offer.kind === 'paid') {
      // The IAP sheet takes over; advance past this card immediately.
      this.deps.onPaid(offer);
      this.advance();
      return;
    }

    if (offer.kind === 'free') {
      this.busy = true;
      this.cardImg.classList.add('busy');
      this.pendingClaimId = offer.id;
      this.deps.onClaim(offer);
      return;
    }

    if (offer.kind === 'video') {
      const ads = offer.video?.ads ?? 0;
      this.busy = true;
      this.cardImg.classList.add('busy');
      let watched = 0;
      for (let i = 0; i < ads; i++) {
        this.setProgress(`Watching ${i + 1} / ${ads}...`);
        const ok = await this.deps.adProvider.watchRewarded();
        if (!ok) break;
        watched += 1;
      }
      if (watched < ads) {
        // Bailed early — no reward; let them retry or skip.
        this.busy = false;
        this.cardImg.classList.remove('busy');
        this.setProgress('Ad skipped');
        return;
      }
      this.setProgress('Collecting...');
      this.pendingClaimId = offer.id;
      this.deps.onClaim(offer, watched);
    }
  }

  /** Feed a server claim result back in; advances on success. */
  notifyClaimResult(result: OfferClaimResult): void {
    if (!this.pendingClaimId || result.offerId !== this.pendingClaimId) return;
    this.busy = false;
    this.cardImg.classList.remove('busy');
    this.pendingClaimId = null;
    if (result.ok) {
      this.advance();
    } else {
      this.setProgress(claimError(result.reason));
    }
  }

  destroy(): void {
    this.element.remove();
  }

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-offerpopup-host {
        position: fixed; inset: 0; z-index: 220;
        display: none; align-items: center; justify-content: center;
        padding: 24px;
        background: rgba(4, 4, 8, 0.82);
        backdrop-filter: blur(7px); -webkit-backdrop-filter: blur(7px);
      }
      .sdl-offerpopup-host.visible { display: flex; }

      .sdl-offerpopup-stage {
        position: relative;
        display: flex; align-items: center; justify-content: center;
        max-width: min(92vw, 460px);
        max-height: 86vh;
        animation: sdl-offerpopup-in 0.28s cubic-bezier(0.34, 1.4, 0.6, 1);
      }
      @keyframes sdl-offerpopup-in {
        from { transform: translateY(20px) scale(0.92); opacity: 0; }
        to   { transform: translateY(0) scale(1); opacity: 1; }
      }

      .sdl-offerpopup-card {
        display: block;
        max-width: 100%; max-height: 86vh;
        width: auto; height: auto;
        border-radius: 14px;
        cursor: pointer;
        filter: drop-shadow(0 24px 60px rgba(0, 0, 0, 0.7));
        -webkit-tap-highlight-color: transparent;
        transition: transform 0.12s ease, filter 0.2s ease;
      }
      .sdl-offerpopup-card:active { transform: scale(0.985); }
      .sdl-offerpopup-card.busy { filter: drop-shadow(0 24px 60px rgba(0, 0, 0, 0.7)) brightness(0.6); pointer-events: none; }

      .sdl-offerpopup-close {
        position: absolute; top: -14px; right: -14px;
        width: 36px; height: 36px;
        background: rgba(20, 20, 26, 0.95);
        border: 1px solid rgba(255, 255, 255, 0.18);
        border-radius: 50%;
        color: rgba(255, 255, 255, 0.75);
        font-size: 24px; line-height: 1;
        cursor: pointer; z-index: 1;
        -webkit-tap-highlight-color: transparent;
        transition: background 0.2s, color 0.2s, transform 0.12s;
      }
      .sdl-offerpopup-close:active { transform: scale(0.92); background: rgba(255, 255, 255, 0.15); color: #fff; }

      .sdl-offerpopup-progress {
        position: absolute; left: 50%; bottom: 18px;
        transform: translateX(-50%);
        display: none;
        padding: 8px 18px;
        background: rgba(8, 8, 12, 0.92);
        border: 1px solid rgba(255, 255, 255, 0.16);
        border-radius: 999px;
        font-family: 'Rajdhani', sans-serif;
        font-size: 14px; font-weight: 600; letter-spacing: 0.5px;
        color: #fff;
      }
      .sdl-offerpopup-progress.show { display: block; }
    `;
    document.head.appendChild(style);
  }
}

function claimError(reason?: string): string {
  switch (reason) {
    case 'already_claimed': return 'Already claimed';
    case 'cooldown': return 'Come back later';
    case 'ineligible': return 'Not available';
    default: return 'Could not claim';
  }
}
